import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Filter, History } from 'lucide-react';

import { Button } from '../components/ui';
import { DataTable, type ColumnDef } from '../components/DataTable';
import { Breadcrumb } from '../components/Breadcrumb';
import {
  ListParserDataFilters,
  type TypedFilters,
} from '../components/listParserData/ListParserDataFilters';
import { MatchesPanel } from '../components/listParserData/MatchesPanel';
import { apiService } from '../services/apiService';
import type {
  ListParserDataItem,
  ListParserDataMatch,
  PriceList,
} from '../services/types';

const DEFAULT_DAYS = 30;
const DEBOUNCE_MS = 400;
const DAY_MS = 24 * 60 * 60 * 1000;

// The API counts days in UTC, so the default range does too
const defaultRange = () => {
  const now = Date.now();
  return {
    from: new Date(now - (DEFAULT_DAYS - 1) * DAY_MS)
      .toISOString()
      .slice(0, 10),
    to: new Date(now).toISOString().slice(0, 10),
  };
};

const rowKey = (row: ListParserDataItem) => `${row.jobId}#${row.rowNumber}`;

// What goes in the brackets after "Yes": the type of a single match, the number when there are several
const matchDetail = (
  matches: ListParserDataMatch[],
  t: (key: string, options?: Record<string, unknown>) => string
): string =>
  matches.length === 1
    ? t(`listParserData.matchTypes.${matches[0].matchType}`, {
        defaultValue: matches[0].matchType,
      })
    : String(matches.length);

export const ListParserDataPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL so a filtered view can be shared and survives a reload
  const defaults = useRef(defaultRange()).current;
  const from = searchParams.get('from') ?? defaults.from;
  const to = searchParams.get('to') ?? defaults.to;
  const partNumber = searchParams.get('part') ?? '';
  const user = searchParams.get('user') ?? '';
  const fileName = searchParams.get('file') ?? '';
  const matchType = searchParams.get('matchType') ?? '';
  // One run (a job id) instead of the dates
  const run = searchParams.get('run') ?? '';
  const machineType = searchParams.get('machine') ?? '';
  const priceList = searchParams.get('priceList') ?? '';
  const matched = searchParams.get('matched') ?? '';
  const search = searchParams.get('q') ?? '';

  // What is typed in the text filters, until a pause turns it into the filter itself
  const [typed, setTyped] = useState<TypedFilters>({
    part: partNumber,
    user,
    file: fileName,
    q: search,
  });
  const type =
    (field: keyof typeof typed) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setTyped((previous) => ({ ...previous, [field]: e.target.value }));
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [rows, setRows] = useState<ListParserDataItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const latestRequest = useRef(0);

  const toggleExpanded = (key: string) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const setFilters = useCallback(
    (changes: Record<string, string>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(changes).forEach(([key, value]) => {
            if (value) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Typing waits for a pause before it becomes a filter (and so a request)
  useEffect(() => {
    const next = {
      part: typed.part.trim(),
      user: typed.user.trim(),
      file: typed.file.trim(),
      q: typed.q.trim(),
    };
    if (
      next.part === partNumber &&
      next.user === user &&
      next.file === fileName &&
      next.q === search
    ) {
      return;
    }
    const timer = setTimeout(() => setFilters(next), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed, partNumber, user, fileName, search, setFilters]);

  useEffect(() => {
    apiService
      .getPriceLists()
      .then(setPriceLists)
      .catch((err) => console.error('Failed to fetch price lists:', err));
  }, []);

  const load = useCallback(
    async (cursor?: string) => {
      const requestId = ++latestRequest.current;
      setIsLoading(true);
      setError(null);
      try {
        const page = await apiService.getListParserData({
          from,
          to,
          partNumber,
          user,
          fileName,
          matchType,
          jobId: run,
          machineType,
          priceList,
          matched,
          search,
          cursor,
        });
        if (requestId !== latestRequest.current) return;
        setRows((previous) =>
          cursor ? [...previous, ...page.items] : page.items
        );
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (requestId !== latestRequest.current) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (requestId === latestRequest.current) setIsLoading(false);
      }
    },
    [
      from,
      to,
      partNumber,
      user,
      fileName,
      matchType,
      run,
      machineType,
      priceList,
      matched,
      search,
    ]
  );

  // A change of filters starts over from the newest row
  useEffect(() => {
    setRows([]);
    setNextCursor(undefined);
    setExpanded(new Set());
    void load();
  }, [load]);

  const columns: ColumnDef<ListParserDataItem>[] = [
    {
      key: 'timestamp',
      header: t('listParserData.columns.timestamp'),
      render: (value) => (
        <span className="whitespace-nowrap">
          {new Date(value as string).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'userName',
      header: t('listParserData.columns.user'),
      render: (value) => (value as string | undefined) || '–',
    },
    {
      key: 'file',
      header: t('listParserData.columns.file'),
      render: (_, row) => (
        <span className="inline-flex items-center gap-1">
          {row?.fileUrl ? (
            <a
              href={row.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {row.fileName || row.file}
            </a>
          ) : (
            row?.fileName || row?.file
          )}
          {row && !run && (
            <button
              type="button"
              title={t('listParserData.showOnlyThisRun')}
              aria-label={t('listParserData.showOnlyThisRun')}
              onClick={() => setFilters({ run: row.jobId })}
              className="text-gray-400 hover:text-blue-600"
            >
              <Filter className="w-4 h-4" />
            </button>
          )}
        </span>
      ),
    },
    { key: 'worksheet', header: t('listParserData.columns.worksheet') },
    { key: 'machineType', header: t('listParserData.columns.machineType') },
    { key: 'priceList', header: t('listParserData.columns.priceList') },
    {
      key: 'discountPct',
      header: t('listParserData.columns.discountPct'),
      align: 'right',
      render: (value) => `${value}%`,
    },
    {
      key: 'partNumCol',
      header: t('listParserData.columns.partNumCol'),
      align: 'right',
    },
    {
      key: 'descCol',
      header: t('listParserData.columns.descCol'),
      align: 'right',
    },
    {
      key: 'qtyCol',
      header: t('listParserData.columns.qtyCol'),
      align: 'right',
    },
    {
      key: 'fileReference',
      header: t('listParserData.columns.fileReference'),
      className: 'font-medium',
    },
    {
      key: 'fileQty',
      header: t('listParserData.columns.fileQty'),
      align: 'right',
    },
    { key: 'fileDesc', header: t('listParserData.columns.fileDesc') },
    {
      key: 'fileRawRow',
      header: t('listParserData.columns.fileRawRow'),
      className: 'font-mono text-xs',
    },
    {
      key: 'matched',
      header: t('listParserData.columns.matched'),
      render: (value, row) => {
        // Rows from before matches were recorded say nothing either way
        if (typeof value !== 'boolean') return '–';
        // Runs from before the table were matched again later: say so, as it is not what the customer saw
        const rematched = row?.rematchedAt && (
          <span
            title={t('listParserData.rematched', {
              date: new Date(row.rematchedAt).toLocaleDateString(),
            })}
            aria-label={t('listParserData.rematched', {
              date: new Date(row.rematchedAt).toLocaleDateString(),
            })}
            className="inline-flex text-amber-600"
          >
            <History className="w-4 h-4" />
          </span>
        );
        if (!value || !row?.matches?.length) {
          return (
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              {value ? t('common.yes') : t('common.no')}
              {rematched}
            </span>
          );
        }
        const open = expanded.has(rowKey(row));
        return (
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <button
              type="button"
              aria-expanded={open}
              onClick={() => toggleExpanded(rowKey(row))}
              className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              {open ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              {t('common.yes')} ({matchDetail(row.matches, t)})
            </button>
            {rematched}
          </span>
        );
      },
    },
    {
      key: 'outputFile',
      header: t('listParserData.columns.outputFile'),
      render: (_, row) =>
        row?.outputFileUrl ? (
          <a
            href={row.outputFileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            {t('listParserData.columns.outputFile')}
          </a>
        ) : (
          '–'
        ),
    },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 w-full">
      <div className="w-full">
        <Breadcrumb currentPage={t('navigation.listParserData')} />

        <div className="mb-6 mt-4">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
            {t('listParserData.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('listParserData.description')}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
            <p className="font-semibold">{t('listParserData.loadError')}</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {run && (
          <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900">
            <span>
              {rows[0]?.fileName
                ? t('listParserData.runBanner', { name: rows[0].fileName })
                : t('listParserData.runBannerUnnamed')}
            </span>
            <Button
              onClick={() => setFilters({ run: '' })}
              variant="outline"
              size="sm"
            >
              {t('listParserData.showAllRuns')}
            </Button>
          </div>
        )}

        <ListParserDataFilters
          filters={{
            from,
            to,
            run,
            machineType,
            matchType,
            priceList,
            matched,
          }}
          typed={typed}
          onType={type}
          onFilter={setFilters}
          priceLists={priceLists}
        />

        <div className="relative">
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={rowKey}
            getRowClassName={(row: ListParserDataItem) =>
              expanded.has(rowKey(row)) ? 'bg-blue-50' : ''
            }
            renderExpandedRow={(row: ListParserDataItem) =>
              expanded.has(rowKey(row)) && row.matches?.length ? (
                <MatchesPanel
                  matches={row.matches}
                  rematchedAt={row.rematchedAt}
                />
              ) : null
            }
            isEmpty={rows.length === 0}
            emptyMessage={
              isLoading ? t('common.loading') : t('listParserData.empty')
            }
          />
          {isLoading && rows.length > 0 && (
            <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-lg" />
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>{t('listParserData.showing', { count: rows.length })}</span>
          {nextCursor && (
            <Button
              onClick={() => void load(nextCursor)}
              disabled={isLoading}
              variant="outline"
              size="sm"
            >
              {rows.length === 0
                ? t('listParserData.keepSearching')
                : t('listParserData.loadMore')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListParserDataPage;
